import { Center, Loader } from '@mantine/core';
import { useParams } from 'react-router';
import { Client } from 'src/admin/users/types';
import { useClients } from '../hooks/useClients';
import { useFamily } from '../hooks/useFamily';
import { getFamilyMemberClientIds } from '../utils';
import { FamilyFlow } from './CreateFamilyPage';
import { V2MemberRelationship, V2MemberRoles } from './dataHelpers';

export default function EditFamilyPage() {
  // Reachable via two routes with different param names: clients/families/:id/edit
  // and family/:familyId/edit. Resolve from whichever is present.
  const { id, familyId } = useParams();
  const { family, isLoading: familyLoading } = useFamily(id ?? familyId);
  const { clients, isLoading: clientsLoading } = useClients();

  if (familyLoading || clientsLoading || !family) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  const clientById = new Map(clients.map((client: Client) => [client.id, client]));
  const initialMembers = getFamilyMemberClientIds(family)
    .map(clientId => clientById.get(clientId))
    .filter((client): client is Client => !!client);

  const initialRoles: V2MemberRoles = {};
  if (family.mainFamilyMember?.familyRoleId) {
    initialRoles[family.mainFamilyMember.clientId] = family.mainFamilyMember.familyRoleId;
  }
  family.members.forEach(member => {
    initialRoles[member.clientId] = member.familyRoleId ?? null;
  });

  const initialRelationships: Record<string, V2MemberRelationship> = {};
  family.relationships.forEach(({ fromClientId, toClientId, relationshipType }) => {
    (initialRelationships[fromClientId] ??= {})[toClientId] = relationshipType;
  });

  return (
    <FamilyFlow
      mode="edit"
      familyId={family.id}
      initialMembers={initialMembers}
      initialName={family.name}
      initialRoles={initialRoles}
      initialRelationships={initialRelationships}
      initialAdvisorUserId={family.advisorUserId}
    />
  );
}
